import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import qs from 'qs';
import DefaultSplashV2 from './components/DefaultSplashV2.tsx';
import DynamicPageTitle from './components/Helmet/DynamicPageTitle';
import IdleModal from './components/Modals/IdleModal.tsx';
import AgentRoute from './components/RestrictedRoute/AgentRoute.tsx';
import AuthRedirectRoute from './components/RestrictedRoute/AuthRedirectRoute.tsx';
import GuestRoute from './components/RestrictedRoute/GuestRoute.tsx';
import ProtectedRoute from './components/RestrictedRoute/ProtectedRoute.tsx';
import ToastNotificationContainer from './components/ToastNotificationContainer';
import useRegisterWindowHeightEffect from './hooks/useRegisterWindowHeightEffect.tsx';
import { OnboardingResponseRoleEnum } from './openapi/yenta';
import AgentChangePasswordRoute from './routes/AgentChangePasswordRoute.tsx';
import AgentEmailSentRoute from './routes/AgentEmailSentRoute.tsx';
import AgentEmailVerificationRequired from './routes/AgentEmailVerificationRequired.tsx';
import AgentEnableMFARouteV2 from './routes/AgentEnableMFARouteV2.tsx';
import AgentForgotPasswordRoute from './routes/AgentForgotPasswordRoute.tsx';
import AgentLoginRoute from './routes/AgentLoginRoute.tsx';
import AgentOnboardingRoute from './routes/AgentOnboardingRoute.tsx';
import AgentPartnerRoute from './routes/AgentPartnerRoute.tsx';
import AgentSignInMFARouteV2 from './routes/AgentSignInMFARouteV2.tsx';
import AgentValidateEmail from './routes/AgentValidateEmail.tsx';
import ChangePasswordRoute from './routes/ChangePasswordRoute';
import ChatRoute from './routes/ChatRoute.tsx';
import EmailSentRoute from './routes/EmailSentRoute';
import EmailVerificationRequired from './routes/EmailVerificationRequired.tsx';
import ForgotPasswordRoute from './routes/ForgotPasswordRoute';
import LoanAdvisorRoute from './routes/LoanAdvisorRoute.tsx';
import LoanTypeRoute from './routes/LoanTypeRoute.tsx';
import LoansRoute from './routes/LoansRoute.tsx';
import OnboardingApplicationRoute from './routes/OnboardingApplicationRoute.tsx';
import OnboardingSignUpRouteV2 from './routes/OnboardingSignUpRouteV2.tsx';
import PreApprovalFormFileCabinetRoute from './routes/PreApprovalFormFileCabinetRoute.tsx';
import PreApprovalFormRoute from './routes/PreApprovalFormRoute';
import PreApprovalFormRoutes from './routes/PreApprovalFormRoutes.tsx';
import PreApprovalFormTaskDetailRoute from './routes/PreApprovalFormTaskDetailRoute.tsx';
import PreApprovalFormTasksRoute from './routes/PreApprovalFormTasksRoute.tsx';
import QRCodeRoute from './routes/QRCodeRoute.tsx';
import RezenAgentSignUpRoute from './routes/RezenAgentSignUpRoute.tsx';
import Route404 from './routes/Route404.tsx';
import SecuritySettingsRoute from './routes/SecuritySettingsRoute.tsx';
import SignInRoute from './routes/SignInRoute';
import SignUpRoute from './routes/SignUpRoute';
import EnableMFARouteV2 from './routes/V2/EnableMFARouteV2.tsx';
import SignInMFARouteV2 from './routes/V2/SignInMFARouteV2.tsx';
import ValidateEmail from './routes/ValidateEmail.tsx';
import ErrorIndex from './routes/secret/ErrorsRoute.tsx';
import { fetchAuthUserDetail } from './slices/AuthSlice';
import { useAppDispatch } from './slices/store';
import { RootState } from './types';
import { getAuthCookie } from './utils/AuthUtils';
import { isRBOFeatureEnabled } from './utils/commonUtils.ts';
import SponsorService from './utils/SponsorUtils';

interface AppProps {}

const App: React.FC<AppProps> = () => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(true);
  const {
    auth: { authUserDetail, loadingAuthUserDetail },
    loan: { loans },
  } = useSelector((state: RootState) => state);
  const isAgentContext = window.location.pathname.includes('/agent');

  const fetchAuth = useCallback(async () => {
    if (getAuthCookie()) {
      await dispatch(fetchAuthUserDetail());
    }
    setLoading(false);
  }, [dispatch]);

  const getHomeRoutePath = () => {
    if (isRBOFeatureEnabled(authUserDetail)) {
      // Check for sponsor info and append to agent-onboarding URL
      const sponsorInfo = SponsorService.getSponsorInfo();
      if (sponsorInfo?.sponsorCode || sponsorInfo?.sponsorName) {
        return `/agent-onboarding?${qs.stringify({
          sponsorCode: sponsorInfo.sponsorCode,
          sponsorName: sponsorInfo.sponsorName,
        })}`;
      }
      return '/agent-onboarding';
    }

    const onboardingApplication = authUserDetail?.onboardings?.find(
      (application) =>
        application.role === OnboardingResponseRoleEnum.TransactionCoordinator,
    );
    if (onboardingApplication?.sherlockApplicationId) {
      return `/onboarding/${onboardingApplication?.sherlockApplicationId}`;
    } else if (authUserDetail) {
      if ((loans || []).length === 0) {
        return `/loan-type`;
      }

      return `/loans`;
    } else {
      if (isAgentContext) {
        return '/agent-login';
      }
      return '/login';
    }
  };

  useEffect(() => {
    fetchAuth();
  }, [fetchAuth]);

  useRegisterWindowHeightEffect();

  if (loading || loadingAuthUserDetail) {
    return (
      <div className='h-screen w-screen'>
        <DefaultSplashV2 />
      </div>
    );
  }

  return (
    <div>
      <BrowserRouter>
        <DynamicPageTitle />
        <Routes>
          <Route path='/redirect' element={<AuthRedirectRoute />} />
          <Route
            path='/'
            element={<Navigate to={getHomeRoutePath()} replace />}
          />
          <Route
            path='/signup'
            element={
              <GuestRoute>
                <SignUpRoute />
              </GuestRoute>
            }
          />
          <Route
            path='/rezen-signup'
            element={
              <GuestRoute pageTitle='One Real: Registration - TCs & Team Admins'>
                <OnboardingSignUpRouteV2 />
              </GuestRoute>
            }
          />
          <Route
            path='/onboarding-signup'
            element={
              <Navigate to={`/rezen-signup${window.location.search}`} replace />
            }
          />
          <Route
            path='/agent-signup'
            element={
              <AgentRoute redirectType='signup'>
                <GuestRoute pageTitle='One Real: Registration - ReZEN Agents'>
                  <RezenAgentSignUpRoute />
                </GuestRoute>
              </AgentRoute>
            }
          />
          <Route
            path='/login'
            element={
              <GuestRoute pageTitle='One Real: Sign In'>
                <SignInRoute />
              </GuestRoute>
            }
          />
          <Route
            path='/agent-login'
            element={
              <AgentRoute redirectType='login'>
                <GuestRoute pageTitle='One Real: Agent Sign In'>
                  <AgentLoginRoute />
                </GuestRoute>
              </AgentRoute>
            }
          />
          <Route
            path='/login/mfa'
            element={
              <GuestRoute>
                <SignInMFARouteV2 />
              </GuestRoute>
            }
          />
          <Route
            path='/agent-login/mfa'
            element={
              <AgentRoute redirectType='login'>
                <GuestRoute>
                  <AgentSignInMFARouteV2 />
                </GuestRoute>
              </AgentRoute>
            }
          />
          <Route
            path='/verify-email'
            element={
              <GuestRoute>
                <EmailVerificationRequired />
              </GuestRoute>
            }
          />
          <Route
            path='/agent-verify-email'
            element={
              <AgentRoute redirectType='login'>
                <GuestRoute>
                  <AgentEmailVerificationRequired />
                </GuestRoute>
              </AgentRoute>
            }
          />
          <Route path='/validate-email' element={<ValidateEmail />} />
          <Route
            path='/agent-validate-email'
            element={
              <AgentRoute redirectType='login'>
                <AgentValidateEmail />
              </AgentRoute>
            }
          />
          <Route path='/enable-mfa' element={<EnableMFARouteV2 />} />
          <Route
            path='/agent-enable-mfa'
            element={
              <AgentRoute redirectType='login'>
                <AgentEnableMFARouteV2 />
              </AgentRoute>
            }
          />
          <Route
            path='/forgot-password'
            element={
              <GuestRoute>
                <ForgotPasswordRoute />
              </GuestRoute>
            }
          />
          <Route
            path='/agent-forgot-password'
            element={
              <AgentRoute redirectType='login'>
                <GuestRoute>
                  <AgentForgotPasswordRoute />
                </GuestRoute>
              </AgentRoute>
            }
          />
          <Route
            path='/change-password'
            element={
              <GuestRoute>
                <ChangePasswordRoute />
              </GuestRoute>
            }
          />
          <Route
            path='/agent-change-password'
            element={
              <AgentRoute redirectType='login'>
                <GuestRoute>
                  <AgentChangePasswordRoute />
                </GuestRoute>
              </AgentRoute>
            }
          />
          <Route
            path='/email-sent'
            element={
              <GuestRoute>
                <EmailSentRoute />
              </GuestRoute>
            }
          />
          <Route
            path='/agent-email-sent'
            element={
              <AgentRoute redirectType='login'>
                <GuestRoute>
                  <AgentEmailSentRoute />
                </GuestRoute>
              </AgentRoute>
            }
          />
          <Route path='/secret/errors' Component={ErrorIndex} />
          <Route
            path='/loan-advisor/:slug'
            element={
              <GuestRoute>
                <LoanAdvisorRoute />
              </GuestRoute>
            }
          />
          <Route
            path='/qrcode'
            element={
              <GuestRoute>
                <QRCodeRoute />
              </GuestRoute>
            }
          />
          <Route
            path='/mortgage-advisor/:slug'
            element={
              <GuestRoute>
                <LoanAdvisorRoute />
              </GuestRoute>
            }
          />
          <Route
            path='/loan-officer/:loanOfficeSlug/partner/:partnerSlug'
            element={
              <GuestRoute>
                <AgentPartnerRoute />
              </GuestRoute>
            }
          />
          <Route
            path='/loan-type'
            element={
              <ProtectedRoute>
                <LoanTypeRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path='/loans'
            element={
              <ProtectedRoute>
                <LoansRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path='/pre-approval-form'
            element={
              <ProtectedRoute>
                <PreApprovalFormRoutes />
              </ProtectedRoute>
            }
          >
            <Route
              path=':borrowerId/tasks/:taskId'
              element={<PreApprovalFormTaskDetailRoute />}
            />
            <Route
              path=':borrowerId/tasks'
              element={<PreApprovalFormTasksRoute />}
            />
            <Route
              path=':borrowerId/file-cabinet'
              element={<PreApprovalFormFileCabinetRoute />}
            />
            <Route path=':borrowerId/chat' element={<ChatRoute />} />
            <Route path=':borrowerId' element={<PreApprovalFormRoute />} />
          </Route>
          <Route
            path='/onboarding/:applicationId'
            element={
              <ProtectedRoute pageTitle='One Real: Registration - TCs & Team Admins'>
                <OnboardingApplicationRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path='/agent-onboarding'
            element={
              <AgentRoute redirectType='login' authenticated>
                <ProtectedRoute pageTitle='One Real: Agent Onboarding'>
                  <AgentOnboardingRoute />
                </ProtectedRoute>
              </AgentRoute>
            }
          />
          <Route
            path='/security-settings'
            element={
              <ProtectedRoute>
                <SecuritySettingsRoute />
              </ProtectedRoute>
            }
          />
          <Route path='*' Component={Route404} />
        </Routes>
        {authUserDetail && <IdleModal />}
      </BrowserRouter>

      <ToastNotificationContainer />
    </div>
  );
};

export default App;
